import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { fetchPageBySlug } from '../lib/cms';
import Gallery from '../components/Gallery';

export default function LogoDesign() {
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function loadPage() {
      const data = await fetchPageBySlug('logodesign');
      setPage(data);
      setLoading(false);
    }
    loadPage();
  }, []);

  if (loading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <div className="h-10 w-10 animate-spin rounded-full border-4 border-brand-red border-t-transparent" />
      </div>
    );
  }

  const heroImage =
    (page?.hero_billede && (Array.isArray(page.hero_billede) ? page.hero_billede[0] : page.hero_billede)) ||
    (page?.hero_image && (Array.isArray(page.hero_image) ? page.hero_image[0] : page.hero_image)) ||
    (page?.billeder && page.billeder[0]) ||
    (page?.images && page.images[0]) ||
    'https://assets.auxodev.dk/assets/83c5e513-aae5-4fee-8b74-db63d3cee346/female-logo-designer-working-graphic-tablet.jpeg';
  const heroImageAlt = page?.hero_billede_alt || page?.hero_image_alt || 'Grafisk designer arbejder på logo';
  const secondaryImage =
    (page?.mellem_billede && (Array.isArray(page.mellem_billede) ? page.mellem_billede[0] : page.mellem_billede)) ||
    (page?.sekundaert_billede && (Array.isArray(page.sekundaert_billede) ? page.sekundaert_billede[0] : page.sekundaert_billede)) ||
    (page?.secondary_image && (Array.isArray(page.secondary_image) ? page.secondary_image[0] : page.secondary_image)) ||
    (page?.billeder && page.billeder[1]) ||
    (page?.images && page.images[1]) ||
    'https://assets.auxodev.dk/assets/83c5e513-aae5-4fee-8b74-db63d3cee346/img-3200.jpeg';
  const secondaryImageAlt =
    page?.mellem_billede_alt ||
    page?.sekundaert_billede_alt ||
    page?.secondary_image_alt ||
    'CRE8IT logo design og grafik';

  return (
    <div className="pb-24 space-y-20 lg:space-y-24 bg-slate-50/40">
      <Helmet>
        <title>{page?.meta_titel || page?.meta_title || 'Logo design & Grafisk Identitet - CRE8IT'}</title>
        <meta name="description" content={page?.meta_beskrivelse || page?.meta_description || 'Få tegnet et unikt og professionelt logo til dit firma. Klar til tryksager, tøj, bildekoration og web.'} />
      </Helmet>

      {/* 1. Hero Sektion */}
      <section className="relative overflow-hidden bg-[#325d52] py-16 lg:py-24 border-b border-[#245b53]/40 text-white">
        {/* Subtile dekorative lysglød */}
        <div className="absolute inset-0 z-0 pointer-events-none">
          <div className="absolute -top-24 -left-24 h-96 w-96 rounded-full bg-white/10 blur-3xl" />
          <div className="absolute -bottom-24 right-10 h-96 w-96 rounded-full bg-[#deecd4]/15 blur-3xl" />
        </div>

        <div className="relative z-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 gap-12 lg:grid-cols-12 lg:items-center">
            
            {/* Venstre kolonne: Tekst */}
            <div className="space-y-6 lg:col-span-7">
              <div className="inline-flex items-center gap-2 rounded-full bg-[#deecd4] px-3.5 py-1.5 text-xs font-bold text-[#325d52] border border-[#deecd4] shadow-xs">
                <i className="fa-solid fa-pen-nib" />
                <span>Grafisk Design & Tryksager</span>
              </div>
              <h1 className="text-4xl font-extrabold tracking-tight text-white sm:text-5xl lg:text-6xl leading-[1.15]">
                {page?.overskrift || page?.headline || (
                  <>
                    DET RIGTIGE <span className="text-[#deecd4]">LOGO</span>
                  </>
                )}
              </h1>
              <div className="flex items-center gap-1.5 pt-0.5">
                <div className="h-1.5 w-12 rounded-full bg-[#deecd4]" />
                <div className="h-1.5 w-3 rounded-full bg-white/40" />
                <div className="h-1.5 w-1.5 rounded-full bg-[#deecd4]" />
              </div>
              <p className="text-lg text-emerald-50/90 leading-relaxed max-w-2xl">
                {page?.underoverskrift || page?.subtitle || 'Det rigtige logo har stor betydning for, hvilket indtryk andre får af dit firma.'}
              </p>
              
              <div className="flex flex-wrap gap-4 pt-2">
                <Link
                  to="/kontakt"
                  className="inline-flex items-center justify-center gap-2 rounded-xl bg-[#deecd4] px-6 py-3.5 text-sm font-bold text-[#1b453f] shadow-md transition-all hover:bg-white hover:text-gray-900 cursor-pointer"
                >
                  <i className="fa-solid fa-envelope text-xs text-[#1b453f]" />
                  Få designet dit logo
                </Link>
                <a
                  href="#galleri"
                  className="inline-flex items-center justify-center gap-2 rounded-xl border-2 border-white/60 bg-white/15 backdrop-blur-xs px-6 py-3.5 text-sm font-semibold text-white shadow-sm transition-all hover:bg-white hover:text-gray-900 cursor-pointer group"
                >
                  <i className="fa-solid fa-images text-[#deecd4] group-hover:text-gray-900 transition-colors" />
                  Se eksempler på logoer
                </a>
              </div>
            </div>

            {/* Højre kolonne: Billede */}
            <div className="relative lg:col-span-5">
              <div className="aspect-[4/3] sm:aspect-square overflow-hidden rounded-3xl border border-gray-200 shadow-xl bg-white">
                <img
                  src={`https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=85,format=auto/${heroImage.replace('https://assets.auxodev.dk/', '')}`}
                  alt={heroImageAlt}
                  className="h-full w-full object-cover"
                />
              </div>
              {/* Dekorativ badge */}
              <div className="absolute -bottom-5 -right-5 rounded-2xl bg-white p-4 shadow-xl border border-gray-100 hidden sm:flex items-center gap-3">
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-brand-green-light/40 text-brand-red">
                  <i className="fa-solid fa-vector-square text-xl" />
                </div>
                <div>
                  <p className="text-xs text-gray-500 font-medium">Vektor-filer</p>
                  <p className="text-sm font-bold text-gray-900">Klar til tryk & web</p>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* 2. Detaljeret Præsentation */}
      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-12 lg:items-center">
          
          <div className="lg:col-span-5 order-last lg:order-first">
            <div className="aspect-[4/3] sm:aspect-[4/5] overflow-hidden rounded-3xl border border-gray-200 shadow-md bg-white flex items-center justify-center">
              <img
                src={`https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=85,format=auto/${secondaryImage.replace('https://assets.auxodev.dk/', '')}`}
                alt={secondaryImageAlt}
                className="h-full w-full object-cover"
              />
            </div>
          </div>

          <div className="lg:col-span-7 space-y-6">
            <span className="text-xs font-bold uppercase tracking-wider text-brand-red">Professionel proces</span>
            <h2 className="text-3xl font-bold text-gray-900 sm:text-4xl">
              Fra idé til <span className="text-brand-red">færdigt, professionelt logo</span>
            </h2>
            <div className="flex items-center gap-1.5 pt-0.5">
              <div className="h-1 w-10 rounded-full bg-brand-red" />
              <div className="h-1 w-2.5 rounded-full bg-brand-green-light" />
            </div>
            
            <div className="prose max-w-none">
              {page?.indhold || page?.content ? (
                <div dangerouslySetInnerHTML={{ __html: page.indhold || page.content }} />
              ) : (
                <>
                  <p>
                    Du har fået en idé til et logo, men kan ikke finde rette løsning til at få det kreeret og er faret vild i de mange forskellige hjemmesider, der mener, de kan designe det for dig.
                  </p>
                  <p>
                    Løsningen er, at vi tegner og skaber det efter dine idéer. Vi kan skabe det logo du ønsker, og som du kan bruge frit bagefter. Dit logo vil blive skabt i forskellige formater som du bagefter kan bruge til tryksager, tøj, hjemmeside, bildekorationer osv.
                  </p>
                  <p>
                    Har du et gammelt logo, der skal genskabes, så det kan skæres i folie eller bruges til andet, er vi klar til at genskabe det. Vi arbejder primært i Corel Draw og Adobe.
                  </p>
                </>
              )}
            </div>
          </div>

        </div>
      </section>

      {/* 3. Highlight Sektion */}
      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="rounded-3xl bg-gradient-to-br from-[#245b53] to-[#173e38] text-white p-8 sm:p-12 lg:p-16 relative overflow-hidden shadow-xl text-center w-full border border-[#1b453f]">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(200,230,201,0.18),transparent_65%)] pointer-events-none" />
          <div className="relative z-10 space-y-6 max-w-3xl mx-auto">
            <span className="text-brand-green-light text-4xl block font-serif">”</span>
            <p className="text-xl sm:text-2xl font-medium leading-relaxed italic text-white">
              Har du et gammelt logo, der skal genskabes, så det kan skæres i folie eller bruges til andet, er vi klar til at genskabe det i skarpe vektorformater.
            </p>
            <div className="h-px w-16 bg-brand-green-light/40 mx-auto" />
            <p className="text-sm uppercase tracking-wider text-brand-green-light font-semibold">Genskabelse & Nytegning</p>
          </div>
        </div>
      </section>

      {/* 4. Specifikationer & Fordele */}
      {(page?.specifikationer || page?.specifications) && (
        <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="rounded-3xl border border-gray-200/90 bg-white p-8 sm:p-12 shadow-xs space-y-6">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-brand-green-light/30 text-brand-red">
                <i className="fa-solid fa-list-check" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900">Grafiske ydelser</h3>
            </div>
            <div className="prose max-w-none" dangerouslySetInnerHTML={{ __html: page.specifikationer || page.specifications }} />
          </div>
        </section>
      )}

      {/* 5. Galleri Sektion */}
      <section id="galleri" className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 border-t border-gray-200/80 pt-16 space-y-8">
        <div className="text-center max-w-2xl mx-auto space-y-2">
          <span className="text-xs font-bold uppercase tracking-wider text-brand-red">Inspiration</span>
          <h2 className="text-3xl font-bold text-gray-900">Se eksempler på logoer og grafik</h2>
          <p className="text-sm text-gray-600">
            Klik på et billede for at se det i stor visning.
          </p>
        </div>
        <Gallery slug="logodesign" />
      </section>
    </div>
  );
}
